import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';

/**
 * Application Insights bootstrap. Initialized lazily after fetching the connection string
 * from `/api/config/client` so the bundle stays free of environment-specific values.
 *
 * Once initialized:
 *  - Auto-tracks page views (including history-change route transitions), AJAX/fetch
 *    requests, and unhandled exceptions.
 *  - Injects W3C `traceparent` headers on outgoing fetch/XHR — the API's CorrelationIdMiddleware
 *    reads them so client + server logs share the same operation_Id.
 *
 * Note: this uses only the core `@microsoft/applicationinsights-web` SDK, not the React
 * plugin (`@microsoft/applicationinsights-react-js`). The plugin's React-19-only peer dep
 * conflicts with the rest of the SPA on React 18, and the route-tracking behavior we
 * actually need works fine via `enableAutoRouteTracking: true` in the core SDK.
 */
let _ai: ApplicationInsights | null = null;
let _initPromise: Promise<ApplicationInsights | null> | null = null;

export function getAppInsights(): ApplicationInsights | null {
  return _ai;
}

/**
 * Idempotent. Fetches `/api/config/client` once, configures AI with the connection string,
 * and resolves with the instance (or null if the server returned no connection string —
 * meaning AI isn't configured for this environment, which is fine for local dev).
 */
export function initAppInsights(): Promise<ApplicationInsights | null> {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const res = await fetch('/api/config/client', { credentials: 'include' });
      if (!res.ok) {
        console.warn(`AppInsights bootstrap: /api/config/client returned ${res.status}; telemetry disabled.`);
        return null;
      }
      const cfg: { appInsightsConnectionString?: string | null; environment?: string } = await res.json();
      if (!cfg.appInsightsConnectionString) {
        // Local dev typically leaves this blank — no AI resource provisioned for the dev loop.
        return null;
      }

      const ai = new ApplicationInsights({
        config: {
          connectionString: cfg.appInsightsConnectionString,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          distributedTracingMode: DistributedTracingModes.W3C,
          enableUnhandledPromiseRejectionTracking: true
        }
      });
      ai.loadAppInsights();
      ai.trackPageView();

      if (cfg.environment) {
        ai.addTelemetryInitializer((envelope) => {
          envelope.tags = envelope.tags || {};
          envelope.tags['ai.cloud.role'] = 'rataexport-spa';
          (envelope.data as Record<string, unknown>) = envelope.data || {};
          (envelope.data as { environment?: string }).environment = cfg.environment;
        });
      }

      _ai = ai;
      return ai;
    } catch (err) {
      console.warn('AppInsights bootstrap failed; telemetry disabled.', err);
      return null;
    }
  })();

  return _initPromise;
}

/**
 * Track an exception with the current AI instance. Safe to call before/without initialization
 * (no-ops when AI isn't configured). Use from `ErrorBoundary.componentDidCatch` and any
 * try/catch you want surfaced in App Insights.
 */
export function trackException(error: unknown, properties?: Record<string, unknown>) {
  if (!_ai) return;
  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown error');
  _ai.trackException({ exception: err, properties });
}
